import { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, ShieldAlert, UserRound } from "lucide-react";

import { useUser } from "context/userContext";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import api from "utils/api";
import { API_ENDPOINTS } from "config/api";
import { extractErrorMessage } from "utils/apiError";
import { isPasswordValid } from "components/common/PasswordRequirements";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { cn } from "lib/utils";
import { setPageTitle } from "utils/pageTitle";
import { toast } from "utils/toast";

function SettingsCard({ icon: Icon, title, description, danger = false, children }) {
  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border bg-card",
        danger ? "border-red-500/35" : "border-border"
      )}
    >
      <div className={cn("flex gap-3 border-b p-5", danger ? "border-red-500/20 bg-red-500/5" : "border-border")}>
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-lg border",
            danger ? "border-red-500/25 bg-red-500/10 text-red-400" : "border-border bg-background text-muted-foreground"
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className={cn("m-0 text-sm font-semibold normal-case tracking-normal", danger ? "text-red-300" : "text-foreground")}>
            {title}
          </h2>
          <p className="m-0 mt-1 text-sm leading-5 text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function PasswordInput({ id, value, onChange, autoComplete, placeholder, required = true }) {
  const { language } = useLanguage();
  const tr = translations[language].settings;
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        className="pe-10"
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="password-toggle absolute inset-y-0 end-0 flex w-10 cursor-pointer items-center justify-center border-0 bg-transparent text-muted-foreground hover:text-foreground"
        aria-label={visible ? tr.hidePassword : tr.showPassword}
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }) {
  return (
    <div className="grid gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={htmlFor}>{label}</Label>
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const { user, onboard, logout } = useUser();
  const { language } = useLanguage();
  const tr = translations[language].settings;
  const [name, setName] = useState(user?.full_name || "");
  const [savingName, setSavingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPageTitle("Settings");
  }, []);

  const trimmedName = name.trim();
  const originalName = (user?.full_name || "").trim();
  const canSaveName = trimmedName.length >= 2 && trimmedName !== originalName;
  const passwordsMatch = newPassword === confirmPassword;
  const canChangePassword = Boolean(currentPassword) && isPasswordValid(newPassword) && passwordsMatch;
  const canDelete =
    Boolean(deletePassword) &&
    Boolean(user?.email) &&
    deleteEmail.trim().toLowerCase() === user.email.toLowerCase() &&
    deleteAcknowledged;

  async function handleNameSubmit(event) {
    event.preventDefault();
    if (!canSaveName) return;
    setSavingName(true);
    try {
      await onboard(trimmedName);
      toast.success(tr.nameUpdated);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingName(false);
    }
  }

  async function handlePasswordSubmit(event) {
    event.preventDefault();
    if (!canChangePassword) return;
    setChangingPassword(true);
    setPasswordError("");
    try {
      await api.post(API_ENDPOINTS.USER.PASSWORD_CHANGE, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(tr.passwordChanged);
      await logout();
    } catch (err) {
      setPasswordError(extractErrorMessage(err));
      setChangingPassword(false);
    }
  }

  function resetDeleteConfirmation() {
    setDeletePassword("");
    setDeleteEmail("");
    setDeleteAcknowledged(false);
    setDeleteError("");
  }

  function handleDeleteOpenChange(open) {
    if (deleting) return;
    setDeleteOpen(open);
    if (!open) resetDeleteConfirmation();
  }

  async function handleDeleteAccount(event) {
    event.preventDefault();
    if (!canDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await api.delete(API_ENDPOINTS.USER.ME);
      setDeleteOpen(false);
      resetDeleteConfirmation();
      toast.success(tr.accountDeleted);
      await logout();
    } catch (err) {
      setDeleteError(extractErrorMessage(err));
      setDeleting(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-8 sm:px-8 md:py-10">
      <div className="mb-7">
        <h1 className="m-0 text-2xl font-semibold text-foreground">{tr.pageTitle}</h1>
        <p className="m-0 mt-1 text-sm text-muted-foreground">{tr.pageSubtitle}</p>
      </div>

      <div className="grid gap-5">
        <SettingsCard
          icon={UserRound}
          title={tr.profileTitle}
          description={tr.profileDescription}
        >
          <form className="grid gap-4" onSubmit={handleNameSubmit}>
            <Field label={tr.fullNameLabel} htmlFor="settings-full-name">
              <Input
                id="settings-full-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                maxLength={150}
                placeholder={tr.fullNamePlaceholder}
              />
            </Field>
            <div className="flex justify-end">
              <Button type="submit" disabled={!canSaveName || savingName}>
                {savingName ? tr.saving : tr.saveName}
              </Button>
            </div>
          </form>
        </SettingsCard>

        <SettingsCard
          icon={KeyRound}
          title={tr.passwordTitle}
          description={tr.passwordDescription}
        >
          <form className="grid gap-4" onSubmit={handlePasswordSubmit}>
            <Field label={tr.currentPasswordLabel} htmlFor="settings-current-password">
              <PasswordInput
                id="settings-current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <Field label={tr.newPasswordLabel} htmlFor="settings-new-password" hint={tr.newPasswordHint}>
              <PasswordInput
                id="settings-new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label={tr.confirmPasswordLabel} htmlFor="settings-confirm-password">
              <PasswordInput
                id="settings-confirm-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
              />
            </Field>
            {confirmPassword && !passwordsMatch ? (
              <p className="m-0 text-xs text-red-400" role="alert">{tr.passwordsMismatch}</p>
            ) : null}
            {passwordError ? (
              <p className="m-0 text-xs text-red-400" role="alert">{passwordError}</p>
            ) : null}
            <p className="m-0 text-xs leading-5 text-muted-foreground">
              {tr.passwordRequirementsNote}
            </p>
            <div className="flex justify-end">
              <Button type="submit" disabled={!canChangePassword || changingPassword}>
                {changingPassword ? tr.changing : tr.changePassword}
              </Button>
            </div>
          </form>
        </SettingsCard>

        <SettingsCard
          icon={ShieldAlert}
          title={tr.dangerZoneTitle}
          description={tr.dangerZoneDescription}
          danger
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="m-0 text-sm font-medium text-foreground">{tr.deleteAccountTitle}</p>
              <p className="m-0 mt-1 text-xs leading-5 text-muted-foreground">{tr.deleteAccountNote}</p>
            </div>
            <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
              {tr.deleteAccountButton}
            </Button>
          </div>
        </SettingsCard>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={handleDeleteOpenChange}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{tr.deleteDialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {tr.deleteDialogDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <form className="grid gap-4" onSubmit={handleDeleteAccount}>
            <Field label={tr.currentPasswordLabel} htmlFor="delete-current-password">
              <PasswordInput
                id="delete-current-password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                autoComplete="current-password"
              />
            </Field>
            <Field label={tr.confirmEmailLabel} htmlFor="delete-email" hint={user?.email}>
              <Input
                id="delete-email"
                type="email"
                value={deleteEmail}
                onChange={(event) => setDeleteEmail(event.target.value)}
                autoComplete="off"
                placeholder={user?.email || "you@example.com"}
                required
              />
            </Field>
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-red-500/20 bg-red-500/5 p-3 text-sm leading-5 text-muted-foreground">
              <input
                type="checkbox"
                checked={deleteAcknowledged}
                onChange={(event) => setDeleteAcknowledged(event.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-red-500"
              />
              <span>{tr.deleteAcknowledgement}</span>
            </label>
            {deleteError ? (
              <p className="m-0 text-xs text-red-400" role="alert">{deleteError}</p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel type="button" disabled={deleting}>{tr.cancel}</AlertDialogCancel>
              <Button type="submit" variant="destructive" disabled={!canDelete || deleting}>
                {deleting ? tr.deleting : tr.permanentlyDeleteAccount}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
